/* eslint-disable react/jsx-props-no-spreading */
// React Dependencies
import React, { useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Popover, IconButton, Tooltip } from '@material-ui/core';
import { toast } from 'react-toastify';
import { isMobile } from 'react-device-detect';

import CropIcon from '@material-ui/icons/Crop';
import VisibilityIcon from '@material-ui/icons/Visibility';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import AttachFileIcon from '@material-ui/icons/AttachFile';

// Internal Dependencies
import { IsEmpty } from '../../../common/checks';
import ProgressiveImage from '../../common/ProgressiveImage';

const SortableMediaItem = ({
  maxHt, minHt, maxWd, minWd, listItemMargin, item, items, pdf, handleCrop, handleView, handleDelete, handleDeletePdf, handleUploadPdf, iconContainerStyles,
  isCropIconVisible, isViewIconVisible, isDeleteIconVisible, disabled,
}) => {
  const [popOpen, setPopOpen] = useState(false);
  const [popAnchor, setPopAnchor] = useState(null);
  const [delConfirmation, setDelConfirmation] = useState(false);
  const [data, setData] = useState(null);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });

  const style = {
    transform:     CSS.Transform.toString(transform),
    transition,
    position:      'relative',
    top:           '0',
    right:         '15px',
    width:         'auto',
    padding:       '10px 15px',
    margin:        listItemMargin || '25px 35px',
    paddingBottom: '50px',
    marginBottom:  '0px',
  };

  const actionContainerStyles = {
    position: 'absolute',
    bottom:   '0',
    right:    isMobile ? '50px' : '20px',
  };

  const iconButtonStyles = {
    backgroundColor: '#ffffff',
    boxShadow:       '0 1px 2px 1px rgba(0, 0, 0, .1)',
    verticalAlign:   'bottom',
    margin:          '0px 5px -20px 0px',
  };

  return (
    <div style={style}>
      <div ref={setNodeRef} {...attributes} {...listeners}>
        <ProgressiveImage
          image={item.display}
          width="100%"
          height="auto"
          draggable={false}
          alt="monstyr"
          style={{
            minHeight:    minHt,
            minWidth:     minWd,
            maxHeight:    maxHt,
            maxWidth:     maxWd,
            resizeMode:   'contain',
            borderRadius: '10px',
            boxShadow:    '0 26px 30px -10px rgb(0 0 0 / 69%), 0 16px 10px -10px rgb(0 0 0 / 73%)',
            objectFit:    'contain',
          }}
        />
        <br />
      </div>

      <div style={{ ...actionContainerStyles, ...iconContainerStyles }}>
        {isCropIconVisible && (
          <Tooltip title="Crop">
            <IconButton
              onClick={() => handleCrop(item)}
              style={iconButtonStyles}
              disabled={disabled}
            >
              <CropIcon />
            </IconButton>
          </Tooltip>
        )}

        {isViewIconVisible && (
          <Tooltip title="View">
            <IconButton
              onClick={() => handleView(item)}
              style={iconButtonStyles}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        )}

        {isDeleteIconVisible && (
          <Tooltip title="Delete">
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                handleDelete(item);
              }}
              style={iconButtonStyles}
              disabled={disabled}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        )}

        {pdf && (
          <>
            {!item.pdf ? (
              <Tooltip title="Upload PDF">
                <IconButton
                  disabled={disabled}
                  onClick={() => {
                    const hasEmptyDisplay = items.some((val) => IsEmpty(val.display));

                    if (hasEmptyDisplay) {
                      toast.error('Please crop all the image first before upload pdf file');
                    } else {
                      handleUploadPdf(item);
                    }
                  }}
                  style={iconButtonStyles}
                >
                  <AttachFileIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <>
                <Tooltip title="View/Delete PDF">
                  <IconButton
                    aria-describedby={popOpen ? 'popId' : undefined}
                    onClick={(event) => {
                      setPopAnchor(event.currentTarget);
                      setPopOpen(true);
                      setData(item);
                    }}
                    style={iconButtonStyles}
                  >
                    <PictureAsPdfIcon />
                  </IconButton>
                </Tooltip>

                <Popover
                  id={popOpen ? 'popId' : undefined}
                  open={popOpen}
                  anchorEl={popAnchor}
                  onClose={() => {
                    setPopOpen(false);
                    setPopAnchor(null);
                    setDelConfirmation(false);
                    setData(null);
                  }}
                  anchorOrigin={{
                    vertical:   'bottom',
                    horizontal: 'left',
                  }}
                >
                  {!delConfirmation
                    ? (
                      <div className="p-2">
                        <Button variant="contained" className="mr-4" size="medium" onClick={() => window.open(data.pdf)} color="default">View</Button>
                        <Button
                          variant="contained"
                          disabled={disabled}
                          size="medium"
                          color="secondary"
                          onClick={() => setDelConfirmation(true)}
                        >
                          Delete
                        </Button>
                      </div>
                    )
                    : (
                      <div className="p-2">
                        <p className="d-block mb-2">Delete PDF ?</p>
                        <Button
                          variant="contained"
                          className="mr-4"
                          size="medium"
                          color="default"
                          onClick={() => {
                            if (data) {
                              handleDeletePdf(data);
                            }

                            setPopOpen(false);
                            setPopAnchor(null);
                            setDelConfirmation(false);
                            setData(null);
                          }}
                        >
                          YES
                        </Button>
                        <Button
                          variant="contained"
                          size="medium"
                          color="secondary"
                          onClick={() => {
                            setPopOpen(false);
                            setPopAnchor(null);
                            setDelConfirmation(false);
                            setData(null);
                          }}
                        >
                          NO
                        </Button>
                      </div>
                    )}
                </Popover>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SortableMediaItem;

SortableMediaItem.propTypes = {
  item:                PropTypes.shape(Object).isRequired,
  items:               PropTypes.arrayOf(PropTypes.shape(Object)),
  pdf:                 PropTypes.bool,
  listItemMargin:      PropTypes.string,
  iconContainerStyles: PropTypes.shape(Object),
  isCropIconVisible:   PropTypes.bool,
  isViewIconVisible:   PropTypes.bool,
  isDeleteIconVisible: PropTypes.bool,
  disabled:            PropTypes.bool,
  maxHt:               PropTypes.number.isRequired,
  minHt:               PropTypes.number.isRequired,
  maxWd:               PropTypes.number.isRequired,
  minWd:               PropTypes.number.isRequired,
  handleCrop:          PropTypes.func,
  handleView:          PropTypes.func,
  handleDelete:        PropTypes.func,
  handleDeletePdf:     PropTypes.func,
  handleUploadPdf:     PropTypes.func,
};

SortableMediaItem.defaultProps = {
  items:               [],
  pdf:                 false,
  listItemMargin:      null,
  iconContainerStyles: {},
  isCropIconVisible:   true,
  isViewIconVisible:   true,
  isDeleteIconVisible: true,
  disabled:            false,
  handleCrop:          () => { },
  handleView:          () => { },
  handleDelete:        () => { },
  handleDeletePdf:     () => { },
  handleUploadPdf:     () => { },
};
